import type { ToolCallApprovalPayload } from "@/components/assistant/findActiveWriteApproval";
import type { UIMessageChunk } from "ai";
import type { Dispatch, SetStateAction } from "react";

type StreamDataPart = {
  type: string;
  data?: unknown;
};

type ApprovalData = {
  runId?: string;
  toolCallId?: string;
  toolName?: string;
  args?: unknown;
  suspendPayload?: { args?: unknown; toolCallId?: string; toolName?: string };
};

function argsFromApprovalData(data: ApprovalData): unknown {
  if (data.args !== undefined && data.args !== null) return data.args;
  const payload = data.suspendPayload;
  if (payload && "args" in payload) return payload.args;
  return undefined;
}

export function applyWriteToolCallApprovalFromStreamPart({
  part,
  isWriteToolName,
  normalizeProposalInput,
  setApprovalByToolCallId,
}: {
  part: StreamDataPart | UIMessageChunk;
  isWriteToolName: (toolName: string) => boolean;
  normalizeProposalInput: (raw: unknown) => Record<string, unknown>;
  setApprovalByToolCallId: Dispatch<
    SetStateAction<Record<string, ToolCallApprovalPayload>>
  >;
}): void {
  const type = part.type;
  if (type !== "data-tool-call-approval" && type !== "data-tool-call-suspended") {
    return;
  }
  const data = (part as StreamDataPart).data as ApprovalData | undefined;
  if (!data?.runId || !data.toolCallId || !data.toolName) return;
  if (!isWriteToolName(data.toolName)) return;

  const toolCallId = data.toolCallId;
  const rawArgs = argsFromApprovalData(data);
  setApprovalByToolCallId((prev) => ({
    ...prev,
    [toolCallId]: {
      runId: data.runId!,
      toolCallId,
      toolName: data.toolName!,
      args: normalizeProposalInput(rawArgs),
    },
  }));
}

export function createWriteToolCallApprovalStreamHandler({
  isWriteToolName,
  normalizeProposalInput,
  setApprovalByToolCallId,
}: {
  isWriteToolName: (toolName: string) => boolean;
  normalizeProposalInput: (raw: unknown) => Record<string, unknown>;
  setApprovalByToolCallId: Dispatch<
    SetStateAction<Record<string, ToolCallApprovalPayload>>
  >;
}) {
  return (part: StreamDataPart | UIMessageChunk) => {
    applyWriteToolCallApprovalFromStreamPart({
      part,
      isWriteToolName,
      normalizeProposalInput,
      setApprovalByToolCallId,
    });
  };
}
